import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "../../../../hooks/usedebounce";
import {
  ChartListResponse,
  ChartOrderBy,
  ChartStatus,
  ChartTypeEnum,
} from "../../../../models/chart";
import { ChartsService } from "../../../../services/charts-service";
import { RequestPrintService } from "../../../../services/request-print-service";

export type ChartsState = {
  loading: boolean;
  query: string;
  debouncedQuery: string;
  status: ChartStatus;
  orderBy: ChartOrderBy;
  charts: ChartListResponse[];
  wasRequestSent: boolean;
  electionDates: string[];
  selectedElectionDate: string;
  total: number;
  chartType: ChartTypeEnum;

  finishAndInvoice: () => void;
  setSelectedElectionDate: (date: string) => void;
  sendRequest: () => void;
  startNewChart: () => void;
  deleteChart: (id: string) => void;
  downloadChart: (id: string) => void;
  updateStatus: (status: ChartStatus) => void;
  updateOrderBy: (orderBy: ChartOrderBy) => void;
  setQuery: (query: string) => void;
};

export default function useCharts(chartType: ChartTypeEnum): ChartsState {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [query, setQuery] = useState<string>("");
  const [wasRequestSent, setWasRequestSent] = useState<boolean>(false);
  const [status, setStatus] = useState<ChartStatus>("InProgress");
  const [orderBy, setOrderBy] = useState<ChartOrderBy>("modifiedAt");
  const [charts, setCharts] = useState<ChartListResponse[]>([]);
  const [filteredCharts, setFilteredCharts] = useState<ChartListResponse[]>([]);
  const [electionDates, setElectionDates] = useState<string[]>([]);
  const [selectedElectionDate, setSelectedElectionDate] = useState<string>("");
  const [total, setTotal] = useState<number>(0);
  const debouncedQuery = useDebounce(query, 800);

  const getBaseUrl = () => {
    if (chartType === ChartTypeEnum.GENERAL_ELECTION) {
      return "/general";
    } else if (chartType === ChartTypeEnum.NON_PARTISAN) {
      return "/non-partisan";
    } else if (chartType === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "/partisan";
    } else if (chartType === ChartTypeEnum.PRESIDENTIAL_PRIMARY) {
      return "/presidential";
    }
  };

  const getCharts = useCallback(
    async (query: string, status: ChartStatus, orderBy: ChartOrderBy) => {
      if (debouncedQuery) {
        setLoading(true);
      }

      try {
        setLoading(true);
        const newCharts = await ChartsService.getCharts(
          status,
          chartType,
          query,
          orderBy
        );
        setCharts(newCharts);

        const dates = newCharts.reduce((prev: string[], curr) => {
          const date = curr.electionDate.split("T")[0];
          if (prev.includes(date)) return prev;
          else return [...prev, date];
        }, []);

        if (dates.length > 0) {
          setElectionDates(dates);
          setSelectedElectionDate(dates[0]);
        }
      } catch (e) {
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedQuery, chartType]
  );

  useEffect(() => {
    getCharts(query, status, orderBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, status, orderBy, chartType]);

  useEffect(() => {
    const newCharts = charts.filter(
      (chart) => chart.electionDate.split("T")[0] === selectedElectionDate
    );
    setFilteredCharts(newCharts);
    setTotal(((newCharts.length - 1) * 25 || 0) + 45);
  }, [charts, selectedElectionDate]);

  const startNewChart = () => {
    navigate(`${getBaseUrl()}/create-chart`);
  };

  const updateStatus = (newStatus: ChartStatus) => {
    setStatus(newStatus);
    getCharts(query, newStatus, orderBy);
  };

  const updateOrderBy = (newOrderBy: ChartOrderBy) => {
    setOrderBy(newOrderBy);
    getCharts(query, status, newOrderBy);
  };

  const deleteChart = async (id: string) => {
    await ChartsService.deleteChart(id);
    await getCharts(query, status, orderBy);
  };

  const downloadChart = async (id: string) => {
    const link = await ChartsService.getS3Link(id, "download");

    const a = document.createElement("a");
    a.href = link;

    document.body.appendChild(a);
    a.click();
    a.parentNode!.removeChild(a);
  };

  const sendRequest = () => {
    RequestPrintService.requestPrintEmail();
    setWasRequestSent(true);
  };

  const finishAndInvoice = () => {
    navigate("/review-invoice/" + chartType + "/" + selectedElectionDate);
  };

  return {
    loading,
    query,
    debouncedQuery,
    status,
    orderBy,
    charts: status === "InProgress" ? filteredCharts : charts,
    wasRequestSent,
    electionDates,
    selectedElectionDate,
    total,
    chartType,

    finishAndInvoice,
    setSelectedElectionDate,
    sendRequest,
    startNewChart,
    deleteChart,
    downloadChart,
    updateStatus,
    updateOrderBy,
    setQuery,
  };
}
